import { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import Head from 'next/head';
import { useTranslation } from 'next-i18next';

// Utils
import renderBlock from '../src/utils/blocks';
import getServerSideTranslations from '../src/utils/server-side';
import redirectCanonicalSlug from '../src/utils/redirectCanonicalSlug';
import { completePageSlug, pageSlugToTitle, replacePageSlug } from '../src/utils/pageSlug';
// Services
import { getContentTranslationProxy } from '../src/services/content-translations';
// Components
import PageTransition from '../components/templates/PageTransition/PageTransition';
import PageLayout from '../components/templates/PageLayout/PageLayout';
import BreadCrumb from '../components/atoms/BreadCrumb/BreadCrumb';
// Hooks
import useSeoLinks from '../src/hooks/useSeoLinks';
// Constants
import { LOCALE_EN, LOCALE_FR } from '../src/constants';

function Index({
  page, relatedPage, locale, localeRelatedPage,
}) {
  // Hooks
  const { t } = useTranslation();
  const data = useMemo(() => page?.[0] || {}, [page?.[0]]);
  const slugRelatedPage = relatedPage?.data?.[0]?.data?.slug;

  const slugs = {
    [locale]: replacePageSlug(data?.data?.slug, locale),
    [localeRelatedPage]: slugRelatedPage
      ? replacePageSlug(slugRelatedPage, localeRelatedPage) : null,
  };

  const { canonicalUrl, alternateLinks } = useSeoLinks(slugs);
  const breadcrumbItems = [
    {
      name: t('breadcrumb.home'),
      position: 1,
      item: `/${locale}`,
    },
    {
      name: pageSlugToTitle(slugs[locale]),
      position: 2,
      item: null,
    },
  ];

  return (
    <>
      <Head>
        <title>{data?.data?.metaTitle}</title>
        <meta name="description" content={data?.data?.metaDescription} />

        <link rel="canonical" href={canonicalUrl} />
        {alternateLinks.map(({ href, hrefLang }) => (
          <link key={hrefLang} rel="alternate" href={href} hrefLang={hrefLang} />
        ))}
      </Head>

      <PageLayout
        stickyHeader={false}
        header={data?.data?.header?.data}
        footer={data?.data?.footer?.data}
        slugs={slugs}
      >
        <PageTransition>
          {data?.data?.blocks?.length ? data?.data?.blocks.map((block, index) => (
            <Fragment key={`${block.type}-${index}`}>
              {renderBlock(block)}
            </Fragment>
          )) : null}

          <BreadCrumb items={breadcrumbItems} />
        </PageTransition>
      </PageLayout>
    </>
  );
}

Index.propTypes = {
  page: PropTypes.arrayOf(PropTypes.shape({
    data: PropTypes.shape({
      blocks: PropTypes.arrayOf(PropTypes.shape({
        type: PropTypes.string,
        data: PropTypes.shape({}),
      })),
    }),
  })).isRequired,
  relatedPage: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.shape({
      data: PropTypes.shape({
        slug: PropTypes.string,
      }),
    })),
  }).isRequired,
  locale: PropTypes.string.isRequired,
  localeRelatedPage: PropTypes.string.isRequired,
};

export async function getServerSideProps({ locale, query }) {
  const slug = completePageSlug(query?.slug, locale);
  const translations = await getServerSideTranslations(locale);
  const preview = { preview: query.previewMode ? true : null, contentId: query.contentId || null };

  const queryClient = new QueryClient();
  let page = {};
  try {
    page = await queryClient.fetchQuery(
      ['page', { slug, contentTypeId: 'page', locale }],
      () => getContentTranslationProxy({
        slug: !preview.preview ? slug : null,
        contentTypeId: 'page',
        ...preview,
      }, locale, query.previewMode)
        .then(async (res) => {
          if (res?.data?.length) {
            const redirect = redirectCanonicalSlug(res?.data?.[0]?.canonicalSlug, slug, '/');
            if (redirect?.redirect?.destination) return redirect;

            return res?.data;
          }
          throw new Error('notFound');
        }),
    );
  } catch (error) {
    return {
      notFound: true,
    };
  }

  if (page?.redirect) return page;

  const localeRelatedPage = locale === LOCALE_FR ? LOCALE_EN : LOCALE_FR;
  const relatedPage = await queryClient.fetchQuery(
    ['page', { contentId: page?.[0]?.contentId, locale: localeRelatedPage }],
    () => getContentTranslationProxy({ contentId: page?.[0]?.contentId }, localeRelatedPage),
    { enabled: !!page?.[0]?.contentId },
  );

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      page,
      relatedPage,
      localeRelatedPage,
      slug,
      ...translations,
    },
  };
}

export default Index;
